import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import {
	classnames as cx,
	domUtils,
	lodash,
	propsUtils,
	keyboardUtils,
} from '@ba-ui-toolkit/utils';
import SVGIcon from '@ba-ui-toolkit/svgicon';
import closeIcon from '@ba-ui-toolkit/ba-graphics/dist/icons-js/close_16';
import BaButton from '@ba-ui-toolkit/button';
import ToolTip from '@ba-ui-toolkit/tooltip';
import { withContext } from '@ba-ui-toolkit/context';
import styles from './Dialog.scss';
import stringResource from './nls/StringResource';

const { enableFocusLoop, disableFocusLoop, resetFocusLoop } = domUtils;
const { isKey, keyNames } = keyboardUtils;
const { assign, uniqueId } = lodash;
const { sanitize } = propsUtils;

const dialogClassName = styles.dialogClassName.replace(/"/g, '');

function createIcon(icon) {
	if (!icon) {
		return null;
	}
	const iconComponent =
		typeof icon === 'string' ? <SVGIcon focusable={false} iconId={icon} /> : icon;

	return iconComponent;
}

function Header(props) {
	const { children, title, ...others } = props;

	if (typeof children === 'string') {
		return (
			<div {...others} key="Header" className={`${dialogClassName}__header`}>
				<div
					className={`${dialogClassName}__title __header_overflow`}
					role="heading"
					title={title || children}
				>
					{children}
				</div>
			</div>
		);
	}

	return (
		<div {...others} key="Header" className={`${dialogClassName}__header`}>
			<div className={`${dialogClassName}__title`}>{children}</div>
		</div>
	);
}

Header.propTypes = {
	children: PropTypes.node,
	title: PropTypes.string,
};

function SubHeader(props) {
	const { children, title, size, ...others } = props;
	if (typeof children === 'string') {
		return (
			size !== 'small' && (
				<div
					{...others}
					className={`${dialogClassName}__subtitle __header_overflow`}
					title={title || children}
				>
					{children}
				</div>
			)
		);
	}

	return (
		size !== 'small' && (
			<div {...others} className={`${dialogClassName}__subtitle`}>
				{children}
			</div>
		)
	);
}

SubHeader.propTypes = {
	children: PropTypes.node,
	title: PropTypes.string,
};

function Button(props) {
	const { primary, ...others } = props;

	return <BaButton tabIndex={0} button set={primary ? 'primary' : 'tertiary'} {...others} />;
}

Button.propTypes = {
	label: PropTypes.string,
	primary: PropTypes.bool,
	onClick: PropTypes.func,
};

function Body(props) {
	const { children, ...others } = props;

	return (
		<div {...others} className={`${dialogClassName}__content`}>
			{children}
		</div>
	);
}

Body.propTypes = {
	children: PropTypes.node,
};

function Footer(props) {
	const { children, ...others } = props;
	const footerChildren = [];
	let hasNonButtonChild = false;
	let primaryIndex;

	// sort through footer children to search for buttons and assign kind
	[...children].forEach(child => {
		if (child) {
			let footerChild = child;
			if (child.type === Button) {
				if (child.props.primary) {
					primaryIndex = footerChildren.length;
				} else {
					footerChild = React.cloneElement(child, { set: 'secondary' });
				}
			} else if (!hasNonButtonChild) {
				hasNonButtonChild = true;
			}
			footerChildren.push(footerChild);
		}
	});

	// move primary button to be the last child
	primaryIndex >= 0 && footerChildren.push(footerChildren.splice(primaryIndex, 1)[0]);

	let classNames = `${dialogClassName}__footer`;

	if (footerChildren.length) {
		if (footerChildren.length === 2 && !hasNonButtonChild) {
			classNames = cx(classNames, { transactional: true });
		} else {
			classNames = cx(classNames, { progress: true });
		}
	}

	return (
		<div data-tid="ba-common-dialog__footer" {...others} className={classNames}>
			{footerChildren}
		</div>
	);
}

Footer.propTypes = {
	children: PropTypes.node,
};

function getString(value) {
	return stringResource.get(value);
}

class Dialog extends Component {
	static propTypes = {
		/** Choose from two fixed sizes */
		size: PropTypes.oneOf(['small', 'large']),
		/** Custom width in px or % */
		width: PropTypes.string,
		/** Custom min width in px or % */
		minWidth: PropTypes.string,
		/** Custom max width in px or % */
		maxWidth: PropTypes.string,
		/** Close dialog on clicking outside */
		clickaway: PropTypes.bool,
		/** Header, SubHeader, Body and Footer inside the dialog */
		children: PropTypes.node,
		/** Callback function when dialog is closed */
		onClose: PropTypes.func,
		/** Custom style theme */
		theme: PropTypes.string,
		/** Get Locale */
		getLocaleString: PropTypes.func,
		/** Custom class name(s) */
		className: PropTypes.string,
		/** Custom CSS styles */
		style: PropTypes.object,
		/** Non-negative value sets the focus at the target node index */
		startingFocusIndex: PropTypes.number,
		/** aria-label string for initially focused div */
		ariaLabel: PropTypes.string,
	};

	static defaultProps = {
		clickaway: false,
		theme: 'ba-theme-default',
		getLocaleString: getString,
		className: '',
		style: {},
		startingFocusIndex: -1,
	};

	componentDidMount() {
		const { startingFocusIndex, ariaLabel } = this.props;
		document.body.style.overflow = 'hidden';
		enableFocusLoop(this.blockerCell, startingFocusIndex, ariaLabel);
		this.node.addEventListener('click', this._setEventDialogProp);
		this.node.addEventListener('touchStart', this._setEventDialogProp);
	}

	componentDidUpdate() {
		resetFocusLoop(this.blockerCell);
	}

	componentWillUnmount() {
		document.body.style.overflow = '';
		disableFocusLoop(this.blockerCell);
		this.node.removeEventListener('click', this._setEventDialogProp);
		this.node.removeEventListener('touchStart', this._setEventDialogProp);
	}

	_id = uniqueId('ba-common-dialog__');
	modal = React.createRef();
	closeNode = React.createRef();

	_setEventDialogProp = e => {
		e.eventDialog = true;
	};

	_onMenuKeyDown = e => {
		this.modal.current.removeAttribute('hiddenOutline');
		if (this.props.onClose && isKey(e, keyNames.ESCAPE)) {
			e.preventDefault();
			this.props.onClose(e);
		}
	};

	_clickawayHandler = e => {
		if (
			this.props.onClose &&
			this.props.clickaway &&
			!this.blockerCell.contains(e.target) &&
			e.target === this.dialogWrapper
		) {
			this.props.onClose(e);
		}
	};

	_validMinWidth = minWidth => {
		const minWidthValue = minWidth.match(/\d+/);

		if (minWidth.indexOf('%') >= 0 && minWidthValue > 100) {
			return false;
		}

		if (minWidth.indexOf('px') >= 0 && minWidthValue <= 400) {
			return false;
		}

		return true;
	};

	_sortChildren = children => {
		// Sort the child nodes in dialog in following order:
		const ref = ['SubHeader', 'Header', 'Body', 'Footer'];

		// It's possible that a dialog doesn't contain all 4 elements
		// Swap-sorting cannot be used here due to incontinuous indexes

		// Pruning first (with/without SubHeader)
		if (
			children.filter((el, i) => el.type.name === ref[i] || el.type.name === ref[i + 1])
				.length === children.length
		) {
			return children;
		}

		// TODO: Find a better sorting algorithm here
		ref.forEach(name => {
			for (let i = 0; i < children.length; i++) {
				if (children[i].type.name === name) {
					const temp = children[i];
					children.splice(i, 1);
					children.push(temp);
					break;
				}
			}
		});

		return children;
	};

	render() {
		const {
			size,
			width,
			minWidth,
			maxWidth,
			theme,
			getLocaleString,
			className,
			style,
			...others
		} = this.props;
		const icon = createIcon(`${closeIcon.id}`);
		let children = null;
		if (this.props.children && Array.isArray(this.props.children)) {
			const modalElements = this._sortChildren([...this.props.children]);
			children = React.Children.map(modalElements, child =>
				React.cloneElement(child, { size })
			);
		} else {
			children = React.Children.map(this.props.children, child =>
				React.cloneElement(child, { size })
			);
		}
		const validMinWidth = minWidth && this._validMinWidth(minWidth) && minWidth;

		const blockerMenuStyle = {
			width,
			maxWidth,
		};

		const modalMenuStyle = {
			minWidth: validMinWidth,
		};

		return createPortal(
			<div
				className={`${theme}`}
				ref={node => {
					this.node = node;
				}}
			>
				<div
					ref={ref => {
						this.dialogWrapper = ref;
					}}
					onClick={this._clickawayHandler}
					onKeyDown={this._onMenuKeyDown}
					className={`${dialogClassName} ${className}`}
					style={style}
				>
					<div
						style={blockerMenuStyle}
						ref={ref => {
							this.blockerCell = ref;
						}}
						data-tid="ba-common-dialog__blocker"
						className={`${dialogClassName}__blockerCell`}
					>
						<div
							style={modalMenuStyle}
							{...sanitize(others, Dialog)}
							role="dialog"
							aria-modal="true"
							ref={this.modal}
							tabIndex="-1"
							className={`${dialogClassName}__modal ${
								(size === 'small' || size === 'large') && !width ? cx(size) : ''
							}`}
						>
							{children}
							{this.props.onClose && (
								<span ref={this.closeNode} className={`${dialogClassName}__closeX`}>
									<ToolTip orient="top" title={getLocaleString('close')}>
										<Button
											data-tid="ba-common-dialog__close-x"
											icon={icon}
											iconSize="small"
											variant="icon"
											intent="primary"
											onClick={this.props.onClose}
											aria-label={getLocaleString('close')}
										/>
									</ToolTip>
								</span>
							)}
						</div>
					</div>
				</div>
			</div>,
			document.body
		);
	}
}

export default assign(Dialog, {
	Header,
	SubHeader,
	Body,
	Footer,
	Button,
});

export const ThemedDialog = assign(withContext('theme')(Dialog), {
	Header,
	SubHeader,
	Body,
	Footer,
	Button,
});
